<!-- test-table.component.html -->
<ng-container *ngIf="(displayableTests$ | async) as currentDisplayableTests">
  <div class="row-container mt-2 mb-2" *ngIf="currentDisplayableTests && currentDisplayableTests.length > 0">
    <div class="left-content">
      <!-- Hide view mode toggle if in simplified display mode -->
      <button *ngIf="!isSimplifiedDisplayMode" class="btn btn-outline-dark mr-2" (click)="toggleViewMode()">
        {{ isCompactView ? 'Switch to Batch View' : 'Switch to Compact View' }}
      </button>

      <!-- Only show this button if at least one column has a subtable -->
      <button class="btn btn-secondary mr-2" (click)="toggleAllSubTables()"
        *ngIf="hasSubTables(currentDisplayableTests)">
        {{ allSubTablesExpanded ? 'Hide SubTables' : 'Show SubTables' }}
      </button>
    </div>
  </div>

  <div *ngIf="!currentDisplayableTests || currentDisplayableTests.length === 0" class="alert alert-info mt-2">
    No tests available to display.
  </div>

  <ng-container *ngIf="currentDisplayableTests && currentDisplayableTests.length > 0">
    <!-- Compact View: Always shown if isCompactView is true (forced in simplifiedMode) -->
    <div *ngIf="isCompactView" class="test-table-container">
      <table #compactTable class="test-styled-table">
        <thead>
          <tr>
            <!-- Use displayColumns getter which respects @Input() columns -->
            <th *ngFor="let column of displayColumns; let i = index" class="test-table-header"
              [ngStyle]="getColumnStyle(column)">
              {{ column }}
              <div class="column-resize-handle" (mousedown)="startResize($event, i, compactTable)"></div>
            </th>
            <!-- MODIFICATION: Conditionally show Actions column header -->
            <th *ngIf="showActions" class="test-table-header" [ngStyle]="getColumnStyle('Actions')">
              Actions
              <div class="column-resize-handle"></div>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Iterate over currentDisplayableTests -->
          <tr *ngFor="let test of currentDisplayableTests; trackBy: trackByFn">
            <!-- Use displayColumns getter -->
            <td *ngFor="let column of displayColumns" class="test-table-cell" [class.test-table-id]="column === 'ID'"
              [class.expanded-cell]="isSubTable(test.data[column]) && allSubTablesExpanded">
              <ng-container *ngIf="!isSubTable(test.data[column]); else subTableTemplate">
                <ng-container *ngIf="!isArray(test.data[column]); else listTemplate">
                  <span *ngIf="column === 'ID'">{{ test.metadata.sequentialId }}</span>
                  <span *ngIf="column !== 'ID'" [ngStyle]="getCellStyle(column)"
                    [innerHTML]="test.data[column] | safeHtml" (click)="handleDataClick($event, test)">
                  </span>
                </ng-container>
                <ng-template #listTemplate>
                  <ul class="cell-list">
                    <li *ngFor="let item of test.data[column]" [innerHTML]="item | safeHtml"
                      (click)="handleDataClick($event, test)">
                    </li>
                  </ul>
                </ng-template>
              </ng-container>
              <ng-template #subTableTemplate>
                <ng-container *ngIf="!allSubTablesExpanded">
                  <span class="sub-table-summary">{{ getSubTableRowCount(test.data[column]) }} rows</span>
                </ng-container>
                <div [class.d-none]="!allSubTablesExpanded" class="sub-table-wrapper">
                  <table class="sub-table">
                    <thead>
                      <tr>
                        <th *ngFor="let subColumn of getSubTableColumns(test.data[column])" class="sub-table-header">{{
                          subColumn }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of getSubTableRows(test.data[column])" class="sub-table-row">
                        <td *ngFor="let subColumn of getSubTableColumns(test.data[column])" class="sub-table-cell">{{
                          row[subColumn] }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </ng-template>
            </td>
            <!-- MODIFICATION: Conditionally show Actions cell and its content -->
            <td *ngIf="showActions" class="test-table-cell test-table-actions">
              <button (click)="openEditModal(test, editModal)" class="test-table-btn test-table-btn-edit">Edit</button>
              <button (click)="deleteTest(test)" class="test-table-btn test-table-btn-delete">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Batch View: Only shown if NOT isCompactView AND NOT isSimplifiedDisplayMode -->
    <ng-container
      *ngIf="!isCompactView && !isSimplifiedDisplayMode && (groupedByMicroScenarioTests$ | async) as groupedScenarios">
      <div *ngIf="!groupedScenarios || groupedScenarios.length === 0" class="alert alert-info mt-2">
        No tests available for batch view or micro scenario data is not fully loaded.
      </div>

      <ng-container *ngFor="let group of groupedScenarios; trackBy: trackByGroupFn">
        <div class="micro-scenario-group mt-4 p-0 border rounded shadow-sm">
          <app-criterion *ngIf="showMicroScenarioColumn" [microScenario]="group.scenario"
            [displayMode]="'microScenarioSummary'" [compact]="true">
          </app-criterion>

          <div class="test-table-container p-3" *ngIf="group.tests.length > 0">
            <table #batchTableView class="test-styled-table">
              <thead>
                <tr>
                  <!-- Use displayColumns getter -->
                  <th *ngFor="let column of displayColumns; let i = index" class="test-table-header"
                    [ngStyle]="getColumnStyle(column)">
                    {{ column }}
                    <div class="column-resize-handle" (mousedown)="startResize($event, i, batchTableView)"></div>
                  </th>
                  <!-- MODIFICATION: Conditionally show Actions column header -->
                  <th *ngIf="showActions" class="test-table-header" [ngStyle]="getColumnStyle('Actions')">
                    Actions
                    <div class="column-resize-handle"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let test of group.tests; trackBy: trackByFn">
                  <!-- Use displayColumns getter -->
                  <td *ngFor="let column of displayColumns" class="test-table-cell"
                    [class.test-table-id]="column === 'ID'"
                    [class.expanded-cell]="isSubTable(test.data[column]) && allSubTablesExpanded">
                    <ng-container *ngIf="!isSubTable(test.data[column]); else subTableTemplate_batch">
                      <ng-container *ngIf="!isArray(test.data[column]); else listTemplate_batch">
                        <span *ngIf="column === 'ID'">{{ test.metadata.sequentialId }}</span>
                        <span *ngIf="column !== 'ID'" [ngStyle]="getCellStyle(column)"
                          [innerHTML]="test.data[column] | safeHtml" (click)="handleDataClick($event, test)">
                        </span>
                      </ng-container>
                      <ng-template #listTemplate_batch>
                        <ul class="cell-list">
                          <li *ngFor="let item of test.data[column]" [innerHTML]="item | safeHtml"
                            (click)="handleDataClick($event, test)">
                          </li>
                        </ul>
                      </ng-template>
                    </ng-container>
                    <ng-template #subTableTemplate_batch>
                      <ng-container *ngIf="!allSubTablesExpanded">
                        <span class="sub-table-summary">{{ getSubTableRowCount(test.data[column]) }} rows</span>
                      </ng-container>
                      <div [class.d-none]="!allSubTablesExpanded" class="sub-table-wrapper">
                        <table class="sub-table">
                          <thead>
                            <tr>
                              <th *ngFor="let subColumn of getSubTableColumns(test.data[column])"
                                class="sub-table-header">{{ subColumn }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr *ngFor="let row of getSubTableRows(test.data[column])" class="sub-table-row">
                              <td *ngFor="let subColumn of getSubTableColumns(test.data[column])"
                                class="sub-table-cell">{{ row[subColumn] }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </ng-template>
                  </td>
                  <!-- MODIFICATION: Conditionally show Actions cell and its content -->
                  <td *ngIf="showActions" class="test-table-cell test-table-actions">
                    <button (click)="openEditModal(test, editModal)"
                      class="test-table-btn test-table-btn-edit">Edit</button>
                    <button (click)="deleteTest(test)" class="test-table-btn test-table-btn-delete">Delete</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div *ngIf="group.tests.length === 0" class="alert alert-light mt-2 p-3">
            No tests found for this micro scenario.
          </div>
        </div>
      </ng-container>
    </ng-container>
  </ng-container>
</ng-container>

<!-- Edit Modal (unchanged from previous, assuming it's working as intended) -->
<ng-template #editModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title" id="testModalLabel">Edit Test Case</h5>
    <button type="button" class="close" aria-label="Close" (click)="modal.dismiss('Cross click')">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
  <div class="modal-body">
    <div *ngIf="selectedTest">
      <!-- Use displayColumns getter -->
      <div *ngFor="let column of displayColumns" class="form-group">
        <ng-container [ngSwitch]="getFieldType(selectedTest.data[column], column)">
          <!-- Simple inputs -->
          <ng-container *ngSwitchCase="'simple'">
            <label [for]="column">{{ column }}</label>
            <input *ngIf="column === 'ID'" type="text" [id]="column" class="form-control"
              [value]="selectedTest.metadata.sequentialId || 0" readonly>
            <input *ngIf="column !== 'ID'" [type]="getInputType(selectedTest.data[column])" [id]="column"
              class="form-control" [(ngModel)]="selectedTest.data[column]">
          </ng-container>

          <!-- Long text -->
          <ng-container *ngSwitchCase="'longtext'">
            <label [for]="column">{{ column }}</label>
            <textarea [id]="column" class="form-control" [(ngModel)]="selectedTest.data[column]" rows="3">
            </textarea>
          </ng-container>

          <!-- Arrays -->
          <ng-container *ngSwitchCase="'array'">
            <label>{{ column }}</label>
            <div *ngFor="let item of selectedTest.data[column]; let i = index; trackBy: trackByIndex"
              class="input-group mb-2">
              <input type="text" class="form-control" [(ngModel)]="selectedTest.data[column][i]">
              <div class="input-group-append">
                <button class="btn btn-outline-danger" type="button" (click)="removeArrayItem(column, i)">
                  Remove
                </button>
              </div>
            </div>
            <button class="btn btn-outline-primary" (click)="addArrayItem(column)">
              Add Item
            </button>
          </ng-container>

          <!-- Sub-tables -->
          <ng-container *ngSwitchCase="'subtable'">
            <label>{{ column }}</label>
            <table class="table table-bordered">
              <thead>
                <tr>
                  <!-- Use getSubTableColumns -->
                  <th *ngFor="let header of getSubTableColumns(selectedTest.data[column])">
                    {{ header }}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <!-- Use getSubTableRows and getSubTableColumns -->
                <tr
                  *ngFor="let row of getSubTableRows(selectedTest.data[column]); let i = index; trackBy: trackByIndex">
                  <td *ngFor="let header of getSubTableColumns(selectedTest.data[column])">
                    <input type="text" class="form-control" [(ngModel)]="row[header]">
                  </td>
                  <td>
                    <button class="btn btn-outline-danger" (click)="removeSubTableRow(column, i)">
                      Remove
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <button class="btn btn-outline-primary" (click)="addSubTableRow(column)">
              Add Row
            </button>
          </ng-container>
        </ng-container>
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss('Close click')">
      Close
    </button>
    <button type="button" class="btn btn-primary" (click)="saveEditedTest()">
      Save changes
    </button>
  </div>
</ng-template>

<!-- REFACTORED PARAMETER DETAIL MODAL -->
<ng-template #parameterModal let-modal>
  <!-- Header: Clean, Flat, Professional Orange -->
  <div class="modal-header-pro">
    <div class="header-content">
      <h5 class="modal-title">
        <span class="param-type-badge">
          {{ selectedParamState?.paramId?.startsWith('CTX') ? 'CONTEXT' : 'INPUT' }}
        </span>
        <span class="param-name">{{ selectedParamState?.paramId }}</span>
      </h5>
      <button type="button" class="btn-close-pro" aria-label="Close" (click)="modal.dismiss()">
        &times;
      </button>
    </div>
  </div>

  <div class="modal-body-pro">
    <div class="pro-grid">

      <!-- LEFT COLUMN: Context & Stats (Sidebar Style) -->
      <div class="pro-sidebar">

        <!-- Section: Definition -->
        <div class="sidebar-section">
          <h6 class="sidebar-heading">Definition</h6>

          <div class="info-block">
            <label>Field Mapping</label>
            <div class="info-value text-break">
              {{ selectedParamState?.definition?.field || 'No field mapping defined' }}
            </div>
          </div>

          <div class="info-block" *ngIf="selectedParamState?.definition?.rules">
            <label>Purpose / Rules</label>
            <div class="info-value text-break">
              {{ getCleanRule(selectedParamState?.definition?.rules) }}
            </div>
          </div>

          <div class="info-block" *ngIf="selectedParamState?.definition?.valid_values?.length">
            <label>Valid Values</label>
            <div class="tags-container">
              <span *ngFor="let v of selectedParamState?.definition?.valid_values" class="tag-pill">
                {{ v }}
              </span>
            </div>
          </div>
        </div>

        <div class="sidebar-divider"></div>

        <!-- Section: Usage Stats -->
        <div class="sidebar-section">
          <h6 class="sidebar-heading">Usage Statistics</h6>

          <div class="stat-row">
            <span class="stat-label">Total Impact</span>
            <span class="stat-number">{{ selectedParamState?.usageCount }} <small>tests</small></span>
          </div>

          <div class="info-block mt-3">
            <label>Distinct Values in Use</label>
            <div class="distinct-values-list">
              <div *ngFor="let val of selectedParamState?.distinctValues" class="value-row"
                [class.is-current]="val === selectedParamState?.currentValue">
                <span class="val-text">{{ val }}</span>
                <span class="current-indicator" *ngIf="val === selectedParamState?.currentValue">(Current)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Edit Actions (Main Content) -->
      <div class="pro-main-content">

        <div class="action-header">
          <h6 class="section-title">Refactor Parameter Value</h6>
          <p class="section-desc">Update the value for this parameter. Select the scope of the change below.</p>
        </div>

        <!-- Input Area -->
        <div class="input-wrapper">
          <label for="paramValueInput">New Value</label>
          <div class="input-group-pro">
            <input type="text" id="paramValueInput" [(ngModel)]="editValue" placeholder="Enter new value..."
              class="form-input-hero">
            <button class="btn-reset" type="button" (click)="editValue = selectedParamState?.currentValue || ''"
              title="Reset to original value">
              Reset
            </button>
          </div>
          <div class="original-value-hint">
            Original: <span class="code-snippet">{{ selectedParamState?.currentValue }}</span>
          </div>
        </div>

        <!-- Scope Selection (Card Style) -->
        <div class="scope-selection">
          <label class="scope-label">Update Scope</label>

          <!-- Option 1: Selective -->
          <label class="scope-card" [class.selected]="editScope === 'single'">
            <div class="scope-radio">
              <input type="radio" name="editScope" value="single" [(ngModel)]="editScope">
            </div>
            <div class="scope-details">
              <span class="scope-title">Selective Fix</span>
              <span class="scope-desc">Update only this specific test case (#{{ selectedTest?.metadata?.sequentialId
                }}).</span>
            </div>
          </label>

          <!-- Option 2: Value Sync -->
          <label class="scope-card" [class.selected]="editScope === 'all_same_value'">
            <div class="scope-radio">
              <input type="radio" name="editScope" value="all_same_value" [(ngModel)]="editScope">
            </div>
            <div class="scope-details">
              <span class="scope-title">Value Sync (Smart)</span>
              <span class="scope-desc">Update all tests currently using exactly <strong>"{{
                  selectedParamState?.currentValue }}"</strong>.</span>
            </div>
            <div class="scope-badge">{{ selectedParamState?.usageCount }} matches</div>
          </label>

          <!-- Option 3: Global Refactor -->
          <label class="scope-card warning-level" [class.selected]="editScope === 'all_occurrences'">
            <div class="scope-radio">
              <input type="radio" name="editScope" value="all_occurrences" [(ngModel)]="editScope">
            </div>
            <div class="scope-details">
              <span class="scope-title">Global Refactor</span>
              <span class="scope-desc">Force update <strong>every</strong> occurrence of this parameter ID, regardless
                of current value.</span>
            </div>
          </label>

        </div>
      </div>

    </div>
  </div>

  <div class="modal-footer-pro">
    <button type="button" class="btn-cancel" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn-save" (click)="saveParameterChange()">
      Apply Changes
    </button>
  </div>
</ng-template>